import React from 'react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Alert } from 'reactstrap';
import { useLocation } from 'react-router-dom';

// RadialProgress component remains the same
const RadialProgress = ({ value, size = 60, strokeWidth = 6 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const progress = ((10 - value) / 10) * circumference;

    const getColor = (score) => {
        if (score >= 9.0) return "#EF4444";
        if (score >= 7.0) return "#F97316";
        if (score >= 4.0) return "#EAB308";
        return "#3B82F6";
    };

    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={getColor(value)}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={progress}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
                <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={`${size / 4}px`}
                    fontWeight="bold"
                    fill="currentColor"
                >
                    {value.toFixed(1)}
                </text>
            </svg>
        </div>
    );
};

const StatusBadge = ({ color, children }) => (
    <Badge color={color} pill className="px-4 py-2 text-md" style={{ fontSize: '.9rem' }}>
        {children}
    </Badge>
);

const ExternalLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary">
        {children} <i className="bx bx-link-external ms-1" />
    </a>
);

const VulnerabilityDashboard = () => {
    const location = useLocation(); // Use the useLocation hook to get the location object
  const { data } = location.state || {};
  console.log(data);

    if (!data) {
        return <div>No vulnerability data available</div>;
    }
    console.log(data);
    const convertDescriptionToLinks = (text) => {
        const replacements = [
            {
                regex: /https?:\/\/[^\s]+/g,
                convertToLink: (match) => `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-primary">Link</a>`,
            },
            {
                regex: /\bCVE-\d{4}-\d{4,7}\b/g,
                convertToLink: (match) => `<a href="https://cve.mitre.org/cgi-bin/cvename.cgi?name=${match}" target="_blank" rel="noopener noreferrer" class="text-primary">${match}</a>`,
            },
            {
                regex: /open redirect/g,
                convertToLink: () => `<a href="https://cwe.mitre.org/data/definitions/601.html" target="_blank" rel="noopener noreferrer" class="text-primary">open redirect</a>`,
            },
        ];

        let transformedText = text;
        transformedText = transformedText.replace(replacements[0].regex, (match) => replacements[0].convertToLink(match));
        transformedText = transformedText.replace(replacements[1].regex, (match) => replacements[1].convertToLink(match));
        transformedText = transformedText.replace(replacements[2].regex, () => replacements[2].convertToLink());

        return <span dangerouslySetInnerHTML={{ __html: transformedText }} />;
    };

    const getSeverityColor = (severity) => {
        switch (severity.toLowerCase()) {
            case 'critical': return 'danger';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'primary';
            default: return 'secondary';
        }
    };

    const { vulnerability, artifact, relatedVulnerabilities } = data;
    const cvssData = vulnerability.cvss[0];

    return (
        <div className="page-content">
            <Card className="shadow">
                <CardHeader className="border-bottom">
                    <CardTitle tag="h1" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                        <i className="bx bx-shield me-2 text-warning" />
                        <ExternalLink href={vulnerability.dataSource}>
                            {vulnerability.id}
                        </ExternalLink>
                    </CardTitle>
                </CardHeader>

                <CardBody>
                    {vulnerability.fix && (
                        <Alert color="success" className="mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="alert-heading mb-2" style={{ fontSize: '1.25rem' }}>Fix Available</h5>
                                    <p className="mb-0">
                                        Upgrade to version {vulnerability.fix.versions[0]} or later
                                    </p>
                                </div>
                                <i className="bx bx-check-circle fs-4" />
                            </div>
                        </Alert>
                    )}

                    <div className="mb-4 d-flex flex-wrap gap-4">
                        <StatusBadge color={getSeverityColor(vulnerability.severity)}>
                            Severity: {vulnerability.severity}
                        </StatusBadge>
                        <StatusBadge color="success">
                            {vulnerability.fix?.state === 'fixed' ? 'Resolvable' : 'Not Resolvable'}
                        </StatusBadge>
                        <StatusBadge color="info">
                            CVSS: {cvssData.metrics.baseScore}
                        </StatusBadge>
                    </div>

                    <Card className="bg-light mb-4">
                        <CardBody>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h5 className="d-flex align-items-center" style={{ fontSize: '1.2rem' }}>
                                        <i className="bx bx-git-branch me-3" />
                                        {artifact.name}
                                    </h5>
                                    <p className="text-muted mb-2">Version: {artifact.version}</p>
                                    <p className="text-muted mb-0">{artifact.purl}</p>
                                </div>
                                <div>
                                    <StatusBadge color="secondary">
                                        {artifact.licenses[0]}
                                    </StatusBadge>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    <Alert color="warning" className="mb-4">
                        <i className="bx bx-error-circle me-3" />
                        {vulnerability.description}
                    </Alert>

                    <Card className="bg-light mb-4">
                        <CardBody>
                            <h3 className="mb-4">CVSS Metrics</h3>
                            <div className="row text-center">
                                <div className="col-md-4">
                                    <RadialProgress value={cvssData.metrics.baseScore} />
                                    <p className="mt-3">Base Score</p>
                                </div>
                                <div className="col-md-4">
                                    <RadialProgress value={cvssData.metrics.exploitabilityScore} />
                                    <p className="mt-3">Exploitability</p>
                                </div>
                                <div className="col-md-4">
                                    <RadialProgress value={cvssData.metrics.impactScore} />
                                    <p className="mt-3">Impact</p>
                                </div>
                            </div>
                            <div className="mt-4">
                                <p className="mb-2">Vector String:</p>
                                <code className="bg-light p-3 d-block" style={{ fontSize: '1rem' }}>
                                    {cvssData.vector}
                                </code>
                            </div>
                        </CardBody>
                    </Card>

                    {relatedVulnerabilities.length > 0 && (
                        <div>
                            <h3 className="mb-4">Related Vulnerabilities</h3>
                            {relatedVulnerabilities.map((vuln, index) => (
                                <Card key={index} className="bg-light mb-4">
                                    <CardBody className="d-flex">
                                        <i className="bx bx-link-alt mt-1 me-3" />
                                        <div>
                                            <div className="d-flex align-items-center mb-3">
                                                <h6 className="mb-0 me-3" style={{ fontSize: '1.2rem' }}>
                                                    <ExternalLink href={vuln.dataSource}>
                                                        {vuln.id}
                                                    </ExternalLink>
                                                </h6>
                                                <span
                                                    className={`text-uppercase text-${getSeverityColor(vuln.severity)}`}
                                                    style={{
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {vuln.severity}
                                                </span>
                                            </div>

                                            <p className="text-muted mb-2">
                                                {convertDescriptionToLinks(vuln.description)}
                                            </p>
                                            {vuln.urls && vuln.urls.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="mb-1">Related Links:</p>
                                                    <ul className="list-unstyled">
                                                        {vuln.urls.map((url, urlIndex) => (
                                                            <li key={urlIndex}>
                                                                <ExternalLink href={url}>
                                                                    {url.split('/').slice(-2).join('/')}
                                                                </ExternalLink>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default VulnerabilityDashboard;
